/**
 * 字体档并入 L2「配色」(V0 定案 3.2.1)—— 把一个死字段救活。
 *
 * ═══ 改之前的样子 ═══════════════════════════════════════════════════════════════
 * `styleTokens.fontPair` 恒为 `{title:"modern_sans", body:"modern_sans"}`
 * (`styleTokenPresets.ts` 里 6 套预设写死、`canvas.ts:safeDefaultStyleTokens` 也写死)
 * —— 一个从来不变的字段,等于没有。
 * 而自由创作那几个字体档(`fontStyleOptions.ts`)只在**单帧**路径注入 prompt,长图完全没有。
 * 同一个"字体感觉"的选择,一条路有、一条路没有,还各存各的。
 *
 * 并进 `fontPair` 之后:**一处选择,单帧与长图两条路径都生效**,死字段也活了。
 *
 * ★为什么直接写字符串就行(ai 零改):`style_tokens.py:78-80` 的
 *   `FontPair{title, body: str, max_length=40}` 是**自由字符串不是枚举** —— 已核实。
 */
import { FONT_STYLE_AUTO } from "../../ui/components/free-create/fontStyleOptions";
import type { StyleTokens } from "../../longimage/model/canvas";

/** 预设默认(= 改之前那个死值);auto 档与未知 key 都落这里。 */
export const DEFAULT_FONT_PAIR: StyleTokens["fontPair"] = {
  title: "modern_sans",
  body: "modern_sans",
};

/**
 * 字体档 key → `fontPair`。
 * ★值是给引擎读的字形描述,与 `fontStyleOptions` 的 key 同名(不另立一套命名,免得漂)。
 * ★标题与正文给同一档:现有 4 个档说的都是"整张图的字长什么样",没有分标题/正文的语义;
 *   真要分得先在 UI 上给两个选择器,那是另一回事。
 */
const FONT_PAIR_BY_KEY: Record<string, StyleTokens["fontPair"]> = {
  rounded: { title: "rounded_sans", body: "rounded_sans" },
  handwritten: { title: "handwritten_script", body: "handwritten_script" },
  serif: { title: "serif", body: "serif" },
  sans: { title: "modern_sans", body: "modern_sans" },
};

export function fontPairForStyle(fontStyleKey?: string | null): StyleTokens["fontPair"] {
  if (!fontStyleKey || fontStyleKey === FONT_STYLE_AUTO) return { ...DEFAULT_FONT_PAIR };
  return { ...(FONT_PAIR_BY_KEY[fontStyleKey] ?? DEFAULT_FONT_PAIR) };
}

/** 反查:`fontPair` → 字体档 key(面板回显选中态用);对不上任何档 → auto。 */
export function fontStyleKeyOf(pair: StyleTokens["fontPair"] | undefined): string {
  if (!pair) return FONT_STYLE_AUTO;
  const hit = Object.entries(FONT_PAIR_BY_KEY).find(
    ([, v]) => v.title === pair.title && v.body === pair.body
  );
  // modern_sans 同时是 sans 档与默认值 —— 回显成 auto(用户没主动选过就不该显示选中)
  if (!hit || hit[0] === "sans") return FONT_STYLE_AUTO;
  return hit[0];
}
